import Layout from "@/components/elements/Layout";
import useAuth from "@/hooks/auth";
import {
  checkSubscription,
  getCampaignContent,
  getListCampaigns,
} from "@/services/api/Newsletter";
import { CalendarBlank, CircleNotch } from "@phosphor-icons/react/dist/ssr";
import React, { forwardRef, useEffect, useState } from "react";

import LockedContent from "@/components/LockedContent";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import NewsletterArchiveSubscribe from "@/components/NewsletterArchiveSubscribe";

export default function EmailArchive() {
  let { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [offset, setOffset] = useState(0);
  const [count, setCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isSubscribe, setIsSubscribe] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [pendingMessage, setPendingMessage] = useState("");

  const isGuest = !user?.id;

  //   user = {
  //     id: 123123,
  //     email: "naufal@lucidityinsights.com",
  //   };

  const handleChange = ([newStartDate, newEndDate]) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);

    // Only proceed if both dates are selected or both are null (clear filter)
    if (
      (newStartDate && newEndDate) ||
      (newStartDate === null && newEndDate === null)
    ) {
      // Reset state
      setCampaigns([]);
      setOffset(0);
      setHasMore(true);

      // Load with new filters
      loadMore(true, newStartDate, newEndDate);
    }
  };

  const CustomInputDatePicker = forwardRef(({ value, onClick }, ref) => (
    <button
      className='flex items-center w-full h-10 gap-2.5 px-3 text-sm text-left border rounded-md bg-white/5 border-white/5'
      onClick={onClick}
      ref={ref}
    >
      <CalendarBlank className='text-base opacity-30' />
      <span className='opacity-70'>{value ? value : "Filter by Month"}</span>
    </button>
  ));

  CustomInputDatePicker.displayName = "CustomInputDatePicker";

  const loadMore = async (
    reset = false,
    customStartDate = startDate,
    customEndDate = endDate
  ) => {
    // Don't fetch if we have only one date selected
    if (
      (customStartDate && !customEndDate) ||
      (!customStartDate && customEndDate)
    ) {
      return;
    }

    if (loading || (!hasMore && !reset)) return;
    setLoading(true);

    try {
      const newCampaigns = await getListCampaigns({
        params: {
          offset: reset ? 0 : offset,
          count: reset ? 10 : count,
          folder_id: "d723bc4598",
          month_start: customStartDate
            ? dayjs(customStartDate).format("YYYY-MM")
            : null,
          month_end: customEndDate
            ? dayjs(customEndDate).format("YYYY-MM")
            : null,
        },
      });

      const campaignList = newCampaigns.data.data.campaigns || [];

      if (campaignList.length === 0) {
        setHasMore(false);
        setLoading(false);
        return;
      }

      const campaignsWithContent = await Promise.all(
        campaignList
          .filter((c) => !campaigns.some((existing) => existing.id === c.id))
          .map(async (campaign) => {
            try {
              const content = await getCampaignContent(campaign.id);
              let htmlContent = content?.data?.data?.html || "";

              const parser = new DOMParser();
              const doc = parser.parseFromString(htmlContent, "text/html");

              const centerTags = doc.body.querySelectorAll(":scope > center");

              // Get the last <center> tag, if any
              const lastCenterTag = centerTags[centerTags.length - 1];

              if (lastCenterTag) {
                // Remove the last <center> tag
                lastCenterTag.remove();
              }

              doc.querySelectorAll("table").forEach((item) => {
                const style = item.getAttribute("style");
                const className = item.getAttribute("class");
                if (className === "xl_border-solid xl_p-4") {
                  item.style.borderLeftWidth = "0px";
                  item.style.borderRightWidth = "0px";
                }
                if (
                  style ===
                  "max-width: 37.5em; margin-top: 1.5rem; margin-bottom: 1.5rem"
                ) {
                  item.remove();
                }
                if (style === "margin-top:20px") {
                  item.remove();
                }
                if (style === "max-width:37.5em;border-top:0 solid #fff") {
                  item.remove();
                }
                if (
                  style === "background-color:#fff;border-top:1px solid #e5e5e5"
                ) {
                  item.remove();
                }
                if (
                  style === "padding:12px 16px;border-bottom:3px solid #f90"
                ) {
                  item.remove();
                }
                if (
                  style === "padding:12px 16px;border-bottom:3px solid #FF9900"
                ) {
                  item.remove();
                }
                if (
                  style ===
                  "display:block;outline:0;border:none;text-decoration:none;width:53%;margin:20px auto"
                ) {
                  item.remove();
                }
                if (style === "max-width:37.5em;border-top:0px solid #FFFFFF") {
                  item.remove();
                }
                if (
                  style ===
                  "background-color:#FFFFFF; border-top:1px solid #E5E5E5;"
                ) {
                  item.remove();
                }
              });
              doc.querySelectorAll("img").forEach((item) => {
                const style = item.getAttribute("style");
                if (
                  style ===
                  "display:block;outline:0;border:none;text-decoration:none;width:53%;margin:20px auto"
                ) {
                  item.remove();
                }
                if (
                  style ===
                  "display:block;outline:none;border:none;text-decoration:none;width:53%;margin:20px auto"
                ) {
                  item.remove();
                }
              });
              doc.querySelectorAll("div").forEach((item) => {
                // Get all direct child <div> elements
                const directDivs = Array.from(item.children).filter(
                  (child) => child.tagName === "DIV"
                );

                // Remove the first 3 direct child <div> elements
                for (let i = 0; i < 2 && i < directDivs.length; i++) {
                  directDivs[i].remove();
                }

                // Remove the last direct child <div>, if it exists
                if (directDivs.length > 0) {
                  directDivs[directDivs.length - 1].remove();
                }

                // Get all direct child <center> elements
                const directCenters = Array.from(item.children).filter(
                  (child) => child.tagName === "CENTER"
                );

                // Remove the last direct child <center>, if it exists
                if (directCenters.length > 0) {
                  directCenters[directCenters.length - 1].remove();
                }

                // Continue with the existing style check for the current <div>
                const style = item.getAttribute("style");
                if (style === "background-color: #333333") {
                  item.style.margin = "-24px";
                }
              });

              htmlContent = doc.body.innerHTML;

              return {
                ...campaign,
                content: { ...content.data.data, html: htmlContent },
              };
            } catch (error) {
              console.error(
                `Error fetching content for campaign ${campaign.id}:`,
                error
              );
              return { ...campaign, content: null };
            }
          })
      );

      setCampaigns((prev) => {
        const uniqueCampaigns = new Map();
        [...prev, ...campaignsWithContent].forEach((c) => {
          uniqueCampaigns.set(c.id, c);
        });
        return Array.from(uniqueCampaigns.values());
      });
      setOffset((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    async function checkAndLoad() {
      try {
        if (user?.id) {
          const subscriber = await checkSubscription(user.email);
          setIsSubscribe(subscriber.data.data.is_subscribed);
          if (subscriber.data.data.status === "pending") {
            setPendingMessage(subscriber.data.data.message);
          }
        } else {
          console.warn("User is not logged in.");
        }

        loadMore();
      } catch (error) {
        console.error("Error during subscription check or data load:", error);
      }
    }

    checkAndLoad();
  }, [user?.id]);

  // Handle infinite scrolling
  useEffect(() => {
    if (isGuest) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;

      if (scrollTop + clientHeight >= scrollHeight - 50) {
        loadMore();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading, hasMore, isGuest]);

  return (
    <Layout title="Newsletter Archive | Lucidity Insights" description="Browse our archive of past newsletters featuring startup insights, investor trends, and ecosystem updates.">
      <div className='min-h-screen px-4 pt-12 mx-auto'>
        <div className='max-w-2xl mx-auto text-center'>
          <h1 className='text-3xl font-bold lg:text-5xl'>Newsletter Archive</h1>
          {pendingMessage && (
            <div className='p-4 mt-4 text-yellow-500 rounded-lg bg-yellow-500/10'>
              {pendingMessage}
            </div>
          )}
          <p className='mt-2 leading-snug lg:mt-4 opacity-80'>
            {`Explore all our past newsletters in one place. Stay informed, revisit key topics, and catch up on updates and stories that matter to you.`}
          </p>
        </div>
        {!isSubscribe && <NewsletterArchiveSubscribe defaultEmail={user?.email} />}
        <div className='max-w-[600px] mx-auto z-20 sticky top-20 py-4 lg:top-[148px] bg-background border-b border-white/10 mb-8 mt-2'>
          <div className='monthpicker'>
            <DatePicker
              selected={startDate}
              onChange={handleChange}
              selectsRange
              startDate={startDate}
              endDate={endDate}
              dateFormat='MMMM yyyy'
              showMonthYearPicker
              isClearable={true}
              customInput={<CustomInputDatePicker />}
            />
          </div>
        </div>
        {campaigns.length > 0 && (
          <div className='relative max-w-[600px] mx-auto -my-6 lg:-my-8 divide-y divide-white/10'>
            {(isGuest ? campaigns.slice(0, 1) : campaigns).map((item) => (
              <div key={item?.id} className='py-6 lg:py-8'>
                <p className='text-xl font-semibold leading-tight tracking-tight lg:text-3xl'>
                  {item.settings.subject_line}
                </p>
                <p className='mt-2 text-sm lg:mt-3 opacity-60'>
                  {dayjs(item.send_time).format("MMMM D, YYYY")}
                </p>
                <div
                  className={
                    isGuest ? "relative max-h-[420px] overflow-hidden" : ""
                  }
                >
                  <div
                    className='p-6 mt-4 font-sans bg-white lg:mt-6 rounded-xl text-background'
                    dangerouslySetInnerHTML={{ __html: item.content.html }}
                  ></div>
                  {isGuest && (
                    <div className='absolute inset-x-0 bottom-0 h-40 pointer-events-none bg-gradient-to-b from-transparent to-background' />
                  )}
                </div>
              </div>
            ))}
            {isGuest && (
              <div className='py-8 !border-t-0'>
                <LockedContent
                  title='Subscribe to Continue Reading'
                  description='Currently you are not subscribed to our newsletter. To access this content, please subscribe and enjoy exclusive benefits available only to our subscribers.'
                  isLogin={user?.id}
                  blur={false}
                />
              </div>
            )}
          </div>
        )}
        {loading && !isGuest && (
          <div className='flex justify-center max-w-[600px] p-4 mx-auto mt-5 mb-5 rounded-lg bg-white/5'>
            <div className='flex items-center gap-2 opacity-40'>
              <CircleNotch className='animate-spin' weight='bold' />
              <span>Loading content...</span>
            </div>
          </div>
        )}
        {!hasMore && !isGuest && (
          <div className='flex justify-center max-w-[600px] p-4 mx-auto mt-5 mb-5 rounded-lg bg-white/10'>
            <div className=''>
              <span>No more Newsletter 🥳</span>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
